import type { CSSProperties } from 'react';
import { useRouter } from 'next/router';
import { useBasePath } from '../lib/use-base-path';
import { useGenericBi } from '../lib/use-generic-bi';
import { useLeaveChatOnNavigate } from '../lib/use-leave-chat';
import { resolveInAppPath } from '../lib/app-link';
import type { EntityKind } from '../lib/entity-link';

// Entity chip — a standalone in-app markdown link (the link is the whole
// paragraph) rendered as a tappable card row: type glyph + the link's text.
// Navigation reuses the same resolveInAppPath routing ordinary in-app links
// get; an in-app link with no web route falls back to its raw href.
// Cross-platform name parity: iOS/Android render the same row as EntityChipRow.
export function EntityChipRow({ href, label, kind }: { href: string; label: string; kind: EntityKind }) {
  const router = useRouter();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const leaveChat = useLeaveChatOnNavigate();
  const inApp = resolveInAppPath(href);
  const target = inApp ? `${basePath}${inApp}` : href;

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    logPageClickBi({ page_name: 'chat', button_name: 'entity_chip_opened', state: kind });
    if (!inApp) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    // The full-screen chat would otherwise sit over the entity the user just opened.
    leaveChat();
    void router.push(inApp, target);
  };

  return (
    <a
      href={target}
      onClick={onClick}
      {...(inApp ? {} : { target: '_blank', rel: 'noreferrer' })}
      style={CHIP_ROW}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(var(--ink-rgb),0.09)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(var(--ink-rgb),0.05)';
      }}
    >
      <span aria-hidden style={GLYPH_WRAP}>
        <EntityGlyph kind={kind} />
      </span>
      <span dir="auto" style={LABEL}>
        {label}
      </span>
      <span aria-hidden style={CHEVRON}>
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
          <path d="M4.5 2.5L8 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </span>
    </a>
  );
}

function EntityGlyph({ kind }: { kind: EntityKind }) {
  switch (kind) {
    case 'task':
      // Checklist
      return (
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden>
          <path d="M2.5 4l1 1 2-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <line x1="7.5" y1="4" x2="13.5" y2="4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          <path d="M2.5 9l1 1 2-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <line x1="7.5" y1="9" x2="13.5" y2="9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          <line x1="7.5" y1="13.5" x2="11.5" y2="13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      );
    case 'event':
      // Calendar
      return (
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden>
          <rect x="2" y="3" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.3" fill="none" />
          <line x1="2" y1="6.5" x2="14" y2="6.5" stroke="currentColor" strokeWidth="1.3" />
          <line x1="5.5" y1="1.5" x2="5.5" y2="4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          <line x1="10.5" y1="1.5" x2="10.5" y2="4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      );
    case 'dashboard':
      // Grid
      return (
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden>
          <rect x="2" y="2" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.3" fill="none" />
          <rect x="9" y="2" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.3" fill="none" />
          <rect x="2" y="9" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.3" fill="none" />
          <rect x="9" y="9" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.3" fill="none" />
        </svg>
      );
    case 'contact':
      // Person
      return (
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden>
          <circle cx="8" cy="5" r="2.8" stroke="currentColor" strokeWidth="1.3" fill="none" />
          <path d="M2.8 13.8a5.4 5.4 0 0 1 10.4 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
        </svg>
      );
    default:
      // Generic in-app arrow
      return (
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden>
          <rect x="2" y="2" width="12" height="12" rx="3" stroke="currentColor" strokeWidth="1.3" fill="none" />
          <path d="M6 10l4-4M7 6h3v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
  }
}

// Quiet elevated row — the attachment-chip visual family (same radius/ink
// tints), attachment-like max-width so it never spans the whole column.
const CHIP_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: 'fit-content',
  maxWidth: 'min(360px, 100%)',
  margin: '0 0 12px',
  padding: '9px 12px',
  background: 'rgba(var(--ink-rgb),0.05)',
  border: '1px solid rgba(var(--hairline-rgb),0.14)',
  borderRadius: 12,
  color: 'rgba(var(--ink-rgb),0.9)',
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '20px',
  transition: 'background 140ms ease',
};

const GLYPH_WRAP: CSSProperties = {
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: 8,
  background: 'rgba(var(--accent-rgb),0.16)',
  color: 'rgba(var(--ink-rgb),0.85)',
};

const LABEL: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const CHEVRON: CSSProperties = {
  flexShrink: 0,
  display: 'inline-flex',
  color: 'rgba(var(--ink-rgb),0.45)',
};
